import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { PhotosGridItem } from '../PhotosGridItem';
import { urlHashes } from '../../../../utils/helpers/constants';
import {
  getModalStyles,
  PhotosGridWrapper,
  ShowAllBtn,
  getModalBackdropStyles,
  CurrentItemTitleWrapper
} from './styled';
import { Photo } from '../../../../graphql/types';
import { Typography } from '../../../ui/Typography';
import { Modal } from '../../../ui/Modal';
import { CurrentItemTitle } from '../../Sections/CurrentItemTitle';

interface PhotosGridProps {
  photos?: Photo[];
  description: ReactNode;
  pageNumber: number;
  isAuthor: boolean;
  isPublished: boolean;
  itemId: string;
  isReported: boolean;
  isFavorited: boolean;
  setFavorited: React.Dispatch<React.SetStateAction<boolean>>;
  onOpenGallery: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    photoNumber?: number
  ) => void;
  setIsAdReported: React.Dispatch<React.SetStateAction<boolean>>;
}

const SCROLL_THRESHOLD = 30;

export const PhotosGrid: React.FC<PhotosGridProps> = ({
  photos,
  description,
  pageNumber,
  isAuthor,
  isPublished,
  itemId,
  isReported,
  isFavorited,
  setFavorited,
  onOpenGallery,
  setIsAdReported
}) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [isGoFromGrid, setIsGoFromGrid] = useState(false);
  const [isOpenGrid, setIsOpenGrid] = useState(false);

  const [isScrolled, setIsScrolled] = useState(false);
  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const pageScrollYRef = useRef<number>(0);

  const openModal = () => {
    pageScrollYRef.current = window.scrollY || window.pageYOffset || 0;
    setIsOpenGrid(true);

    const parsedUrl = new URL(router.asPath, window.location);

    router.push({
      pathname: parsedUrl.pathname,
      hash: urlHashes.photosGrid,
      search: parsedUrl.search
    });
  };

  const closeModal = () => {
    setIsOpenGrid(false);
    router.replace(router.asPath.split('#')[0], undefined, {
      shallow: true,
      scroll: false
    });
  };

  useEffect(() => {
    const parsedUrl = new URL(router.asPath, window.location);
    const isGridOpen =
      parsedUrl.hash === `#${urlHashes.photosGrid}` ||
      (parsedUrl.hash === `#${urlHashes.fullScreenGallery}` && isGoFromGrid);

    setIsOpenGrid(isGridOpen);
  }, [router.asPath]);

  useEffect(() => {
    const el = gridWrapperRef.current;
    if (!el) return;

    const handleScroll = () => {
      const isGridScrolled = el.scrollTop > SCROLL_THRESHOLD;
      const banner = document.getElementById('js-lotriver-desk-top-banner');

      const bannerHeight = banner?.offsetHeight || 0;

      const pageScroll = isOpenGrid
        ? pageScrollYRef.current
        : window.scrollY || window.pageYOffset || 0;

      const isBannerVisible = bannerHeight > 0;
      const isPageScrolled = pageScroll > SCROLL_THRESHOLD;

      setIsScrolled(isGridScrolled || isPageScrolled || isBannerVisible);
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });

    handleScroll();

    return () => {
      el.removeEventListener('scroll', handleScroll);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpenGrid]);

  useEffect(() => {
    if (isOpenGrid) {
      const scrollY = pageScrollYRef.current;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';

      return () => {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        window.scrollTo(0, scrollY);
      };
    }
    setIsGoFromGrid(false);
  }, [isOpenGrid]);

  const modalStyles = getModalStyles(isScrolled, isOpenGrid);
  const modalBackdropStyles = getModalBackdropStyles(isScrolled);

  return (
    <>
      <ShowAllBtn
        onClick={openModal}
        data-cy="show-all-photos"
        data-stat="show-all-photos"
        role="button"
        tabIndex={0}
      >
        <Typography tag="caption_1_new">
          {t('pages.current_item.buttons.all_photos')}
        </Typography>
      </ShowAllBtn>

      <Modal
        isOpen={isOpenGrid}
        modalId="all-photos"
        modalStyles={modalStyles}
        modalBackdropStyles={modalBackdropStyles}
        lockScroll={false}
        header={
          <CurrentItemTitleWrapper>
            <CurrentItemTitle
              title={description}
              isReportButtonShown={!isAuthor}
              isBookmarkedButtonShow={isPublished}
              itemId={itemId}
              isReported={isReported}
              isFavorited={isFavorited}
              setFavorited={setFavorited}
              isPhotosGrid
              handleBack={closeModal}
              setIsAdReported={setIsAdReported}
            />
          </CurrentItemTitleWrapper>
        }
      >
        <PhotosGridWrapper
          ref={gridWrapperRef}
          onTouchStart={() => setIsGoFromGrid(true)}
          $isLessThan3={(photos?.length || 0) < 3}
          data-cy="photos-grid"
        >
          {photos?.map((photo, index, arr) => {
            return (
              <PhotosGridItem
                key={photo.full}
                photo={photo}
                index={index}
                photosCount={arr.length}
                pageNumber={pageNumber}
                onOpenGallery={onOpenGallery}
              />
            );
          })}
        </PhotosGridWrapper>
      </Modal>
    </>
  );
};
